/** @jsx jsx */
import { jsx } from '@emotion/react'
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CacheProvider, css } from '@emotion/react'
import createCache from '@emotion/cache'

const tabHeight = '60px';

type TabButtonBaseProps = {
  image: string;
  offset?: number;
}
const TabButtonBase = styled('div')<TabButtonBaseProps>(({image, offset}) => ({
  width: '100%',
  cursor: 'pointer',
  height: tabHeight,
  background: `url(${image})`,
  backgroundPosition: `-${offset ?? 0}px 0px`,
  backgroundSize: '300%'
}));
type TabButtonProps = {
  image: string;
  onMouseEnter?: any;
  onClick?: any;
  children?: React.ReactNode;
};
export const TabButton: React.FunctionComponent<TabButtonProps> = ({image, ...props}) => {
  const ref = useRef<HTMLDivElement|null>(null);
  return (
    <TabButtonBase ref={ref} offset={ref?.current?.offsetLeft} image={image} {...props} />
  )
}

type TabBarProps = {
}
export const TabBar = styled('div')<TabBarProps>(({}) => ({
  display: 'flex',
  width: '100%',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'row',
  alignContent: 'stretch',
  pageBreakInside: 'avoid',
  gridGap: 0,
  justifyContent: 'stretch',
  height: tabHeight,
  color: 'white',
  textShadow: '5px 5px 10px black',
  textAlign: 'center',
  verticalAlign: 'center',
  lineHeight: tabHeight,
  fontSize: '180%',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MsUserSelect: 'none',
}));
type TabSwitchProps = {
}
// The first panel sets the height of the tab area; the others are laid over it at
// the same size and scroll if they are longer, so switching tabs (which happens
// on hover) never changes the length of the page.
export const TabSwitch = styled('div')<TabSwitchProps>(({}) => ({
  position: 'relative',
  width: '100%',
}));
type TabContentProps = {
  image: string;
  first: boolean;
  active: boolean;
}
export const TabContent = styled('div', {
  shouldForwardProp: (prop) => !['image', 'first', 'active'].includes(prop as string)
})<TabContentProps>(({image, first, active}) => ({
   position: first ? 'relative' : 'absolute',
   inset: first ? undefined : 0,
   overflowY: first ? undefined : 'auto',
   visibility: active ? 'visible' : 'hidden',
   background: `url(${image})`,
   backgroundPosition: `0px -${tabHeight}`,
   backgroundSize: first ? '100%' : 'cover',
   width: '100%',
}));

export type TabProps = {
  title: string
  image: string
}
export type TabsProps = {
  tabs: TabProps[]
  styleRoot: Element
}

export const Tabs = ({tabs, styleRoot}: TabsProps) => {
  const [page, setPage] = useState(0)
  const myCache = createCache({
    key: 'tabs', container: styleRoot
  })
  useEffect(() => {
    return () => {
      myCache.sheet.flush();
      (myCache.inserted as any) = [];
      myCache.registered = {};
    }
  })
  return (
    <CacheProvider value={myCache}>
      <TabBar>
        {tabs.map((tab, idx) =>
          <TabButton key={idx} image={tab.image}
            onMouseEnter={() => setPage(idx)}
            onClick={() => setPage(idx)}
          >{tab.title}</TabButton>)
        }
      </TabBar>
      <TabSwitch>
        {tabs.map((tab, idx) =>
          <TabContent key={idx} image={tab.image} first={idx == 0} active={page == idx} aria-hidden={page != idx}><slot name={`tab-${idx}`} /></TabContent>
        )}
      </TabSwitch>
    </CacheProvider>
  )
}
